import React, { useState } from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { AIMessage, AIToolCall, AIToolResult } from "src/ai/types";
import { TOOL_DISPLAY_NAMES } from "src/ai/tools";

/**
 * Per-message disclosure of every tool call Kate made on the way to the
 * answer. Each call is collapsible: the title row shows what tool ran, and
 * expanding reveals the input (SQL / Python code / arguments) and a preview
 * of the result.
 */

interface Props {
  message: AIMessage;
}

const codeBlockSx = {
  fontFamily: "monospace",
  fontSize: "0.78rem",
  backgroundColor: "#1e1e2e",
  color: "#f5f5f5",
  p: 1,
  borderRadius: 0.5,
  overflow: "auto",
  whiteSpace: "pre",
  maxHeight: 240,
} as const;

const inlinePreviewSx = {
  fontFamily: "monospace",
  fontSize: "0.72rem",
  color: "text.secondary",
  ml: 1,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  flex: 1,
  minWidth: 0,
} as const;

const summarizeInput = (call: AIToolCall): string => {
  if (call.name === "execute_sql") {
    const sql = (call.input.sql as string | undefined) || "";
    return sql.replace(/\s+/g, " ").trim();
  }
  if (call.name === "execute_python") {
    const desc = call.input.description as string | undefined;
    if (desc) return desc;
    const code = (call.input.code as string | undefined) || "";
    return code.split("\n")[0];
  }
  if (call.name === "navigate_to") {
    return String(call.input.path ?? "");
  }
  if (call.name === "get_database_schema") {
    return "";
  }
  try {
    return JSON.stringify(call.input);
  } catch {
    return "";
  }
};

const renderToolInput = (call: AIToolCall): React.ReactNode => {
  if (call.name === "execute_sql") {
    return <Box component="pre" sx={codeBlockSx}>{(call.input.sql as string) || ""}</Box>;
  }
  if (call.name === "execute_python") {
    return <Box component="pre" sx={codeBlockSx}>{(call.input.code as string) || ""}</Box>;
  }
  return (
    <Box component="pre" sx={codeBlockSx}>
      {JSON.stringify(call.input, null, 2)}
    </Box>
  );
};

const renderToolResult = (
  call: AIToolCall,
  result: AIToolResult | undefined,
): React.ReactNode => {
  if (!result) {
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        (no result captured)
      </Typography>
    );
  }
  const r = result.result as Record<string, unknown> | undefined;

  if (call.name === "execute_sql" && r && Array.isArray(r.rows)) {
    const rows = r.rows as unknown[][];
    const columns = (r.columns as Array<{ name: string }> | undefined) ?? [];
    const previewRows = rows.slice(0, 5);
    return (
      <Box>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {`${(r.rowCount as number) ?? rows.length} row(s)`}
          {r.truncated ? " (truncated)" : ""}
          {typeof r.durationMs === "number" ? ` · ${r.durationMs} ms` : ""}
        </Typography>
        {columns.length > 0 && previewRows.length > 0 && (
          <Box sx={{ mt: 0.5, overflow: "auto", maxHeight: 200 }}>
            <Box
              component="table"
              sx={{
                fontSize: "0.72rem",
                borderCollapse: "collapse",
                "& th, & td": {
                  border: "1px solid rgba(0,0,0,0.08)",
                  px: 0.75,
                  py: 0.25,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                },
                "& th": { backgroundColor: "rgba(0,0,0,0.04)", fontWeight: 600 },
              }}
            >
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.name}>{c.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j}>{cell === null ? "NULL" : String(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  if (call.name === "execute_python" && r) {
    const stdout = (r.stdout as string) || "";
    const stderr = (r.stderr as string) || "";
    const images = (r.images as string[] | undefined) ?? [];
    const error = r.error as string | undefined;
    return (
      <Box>
        {error && (
          <Box component="pre" sx={{ ...codeBlockSx, color: "#ff8a80" }}>
            {error}
          </Box>
        )}
        {stdout && (
          <>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              stdout
            </Typography>
            <Box component="pre" sx={codeBlockSx}>{stdout}</Box>
          </>
        )}
        {stderr && (
          <>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              stderr
            </Typography>
            <Box component="pre" sx={{ ...codeBlockSx, color: "#ffb74d" }}>
              {stderr}
            </Box>
          </>
        )}
        {images.length > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {`${images.length} image${images.length === 1 ? "" : "s"} (rendered above)`}
          </Typography>
        )}
      </Box>
    );
  }

  let serialized: string;
  try {
    serialized = JSON.stringify(r ?? result.result, null, 2);
  } catch {
    serialized = String(result.result);
  }
  if (serialized.length > 2000) serialized = serialized.slice(0, 2000) + "\n…";
  return <Box component="pre" sx={codeBlockSx}>{serialized}</Box>;
};

const ToolCallItem: React.FC<{
  call: AIToolCall;
  result: AIToolResult | undefined;
  index: number;
}> = ({ call, result, index }) => {
  const [open, setOpen] = useState(false);
  const display = TOOL_DISPLAY_NAMES[call.name] || call.name;
  const preview = summarizeInput(call);
  const isError = !!result?.isError;

  return (
    <Box
      sx={{
        borderTop: index === 0 ? "none" : "1px solid rgba(0,0,0,0.06)",
        pt: index === 0 ? 0 : 0.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
          borderRadius: 0.5,
          px: 0.5,
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <IconButton size="small" sx={{ p: 0.25 }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Chip
          label={display}
          size="small"
          color={isError ? "error" : "default"}
          sx={{ height: 18, fontSize: "0.7rem", "& .MuiChip-label": { px: 0.75 } }}
        />
        {preview && <Box sx={inlinePreviewSx}>{preview}</Box>}
      </Box>
      {open && (
        <Box sx={{ pl: 3, pr: 0.5, pb: 0.75, pt: 0.25 }}>
          {renderToolInput(call)}
          <Box sx={{ mt: 0.75 }}>{renderToolResult(call, result)}</Box>
        </Box>
      )}
    </Box>
  );
};

export const ToolDetails: React.FC<Props> = ({ message }) => {
  const [open, setOpen] = useState(false);
  const calls = message.toolCalls;
  if (!calls || calls.length === 0) return null;

  const resultsById: Record<string, AIToolResult> = {};
  for (const r of message.toolResults ?? []) {
    resultsById[r.toolCallId] = r;
  }

  return (
    <Box
      sx={{
        mt: 1,
        borderTop: "1px dashed rgba(0,0,0,0.12)",
        pt: 0.5,
        fontSize: "0.78rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          color: "text.secondary",
          userSelect: "none",
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <IconButton size="small" sx={{ p: 0.25 }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Typography variant="caption">
          {`${calls.length} tool call${calls.length === 1 ? "" : "s"}`}
        </Typography>
      </Box>
      {open && (
        <Box sx={{ mt: 0.5 }}>
          {calls.map((call, i) => (
            <ToolCallItem
              key={call.id}
              call={call}
              result={resultsById[call.id]}
              index={i}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
